import React, { useCallback, useEffect, useState } from "react";
import uiConfig from "../config/uiConfig";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import SearchIcon from "@mui/icons-material/Search";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";

import DeleteEmployeeDialog from "../components/employees/DeleteEmployeeDialog";
import EmployeeDialog from "../components/employees/EmployeeDialog";
import EmployeeTable from "../components/employees/EmployeeTable";

import {
  createEmployee,
  deleteEmployee,
  getEmployees,
  updateEmployee,
  syncEmployeesFromAzure,
} from "../services/employeeService";
import { useUserRole } from "../context/UserRoleContext";
import { USER_ROLES } from "../auth/roles";

function Employees() {
  const { role, employee, loading: roleLoading } = useUserRole();

  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleteRelated, setDeleteRelated] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [isSyncingAzure, setIsSyncingAzure] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const loadEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const managerId = role === USER_ROLES.MANAGER ? (employee?.employee_id ?? -1) : null;
      const data = await getEmployees(search, managerId);
      setEmployees(data);

      // Keep only still-valid selected IDs after reload/filter changes.
      setSelectedEmployeeIds((prev) => {
        const validIds = new Set(data.map((employee) => employee.employee_id));
        return prev.filter((id) => validIds.has(id));
      });
    } catch (error) {
      console.error("Employee Error:", error);
      setSnackbar({
        open: true,
        message: "Failed to load employees",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [search, role, employee]);

  useEffect(() => {
    if (roleLoading) {
      return;
    }

    loadEmployees();
  }, [loadEmployees, roleLoading]);

  const handleSyncAzure = async () => {
    if (isSyncingAzure) {
      return;
    }

    try {
      setIsSyncingAzure(true);

      await syncEmployeesFromAzure(employee?.employee_id ?? null);

      setSnackbar({
        open: true,
        message: "Azure AD sync started in the background. We'll notify you once it's done.",
        severity: "success",
      });
    } catch (error) {
      console.error("Azure Sync Error:", error);
      const message = error?.response?.data?.detail || "Failed to start Azure AD sync";

      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      setIsSyncingAzure(false);
    }
  };

  const handleSave = async (data) => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        ...data,
        department_id: Number(data.department_id),
        manager_id: data.manager_id ? Number(data.manager_id) : null,
        role_name: data.role_name || "member",
      };

      if (selectedEmployee) {
        await updateEmployee(selectedEmployee.employee_id, payload);
      } else {
        await createEmployee(payload);
      }

      setDialogOpen(false);
      setSelectedEmployee(null);

      await loadEmployees();

      setSnackbar({
        open: true,
        message: "Employee saved successfully",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Operation failed",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteOpen(false);
    setSelectedEmployee(null);
    setDeleteRelated(false);
  };

  const handleDelete = async () => {
    if (!selectedEmployee || isDeleting) {
      return;
    }

    try {
      setIsDeleting(true);

      await deleteEmployee(selectedEmployee.employee_id, deleteRelated);
      closeDeleteDialog();

      await loadEmployees();

      setSnackbar({
        open: true,
        message: deleteRelated
          ? "Employee and related records deleted successfully"
          : "Employee deleted successfully",
        severity: "success",
      });
    } catch (error) {
      closeDeleteDialog();
      console.error(error);
      const message = error?.response?.data?.detail || "Delete failed";
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEmployeeIds.length === 0 || isBulkDeleting) {
      return;
    }

    try {
      setIsBulkDeleting(true);

      let successCount = 0;
      let failedCount = 0;

      for (const employeeId of selectedEmployeeIds) {
        try {
          await deleteEmployee(employeeId, true);
          successCount += 1;
        } catch (error) {
          failedCount += 1;
        }
      }

      setBulkDeleteOpen(false);

      await loadEmployees();

      setSelectedEmployeeIds([]);

      if (failedCount === 0) {
        setSnackbar({
          open: true,
          message: `${successCount} employee(s) deleted successfully`,
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: `${successCount} deleted, ${failedCount} failed`,
          severity: "warning",
        });
      }
    } catch (error) {
      console.error(error);
      setBulkDeleteOpen(false);
      setSnackbar({
        open: true,
        message: "Bulk delete failed",
        severity: "error",
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        p: 3,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={600} color="text.primary">
            Employees
          </Typography>

          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Manage and view all employees in the organization.
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.2}>
          {role === USER_ROLES.SUPER_ADMIN && (
            <Button
              variant="outlined"
              startIcon={
                isSyncingAzure
                  ? <CircularProgress size={16} color="inherit" />
                  : <CloudSyncOutlinedIcon />
              }
              onClick={handleSyncAzure}
              disabled={isSyncingAzure}
              sx={{
                height: 38,
                textTransform: "none",
                borderRadius: 2,
                px: 2,
              }}
            >
              {isSyncingAzure ? "Syncing..." : "Import from Azure AD"}
            </Button>
          )}

          <Button
            variant="outlined"
            color="error"
            startIcon={
              isBulkDeleting
                ? <CircularProgress size={16} color="inherit" />
                : <DeleteSweepIcon />
            }
            onClick={() => setBulkDeleteOpen(true)}
            disabled={selectedEmployeeIds.length === 0 || isBulkDeleting}
            sx={{
              height: 38,
              textTransform: "none",
              borderRadius: 2,
              px: 2,
            }}
          >
            {isBulkDeleting ? "Deleting..." : `Delete All (${selectedEmployeeIds.length})`}
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedEmployee(null);
              setDialogOpen(true);
            }}
            sx={{
              height: 38,
              background: uiConfig.buttonColor,
              textTransform: "none",
              borderRadius: 2,
              px: 2,
            }}
          >
            Add Employee
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          p: 1.5,
          mb: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search employee name, email or department..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiInputBase-root": {
              height: 38,
            },
          }}
        />
      </Box>

      <EmployeeTable
        employees={employees}
        loading={loading}
        selectedIds={selectedEmployeeIds}
        onSelectionChange={setSelectedEmployeeIds}
        onEdit={(employee) => {
          setSelectedEmployee(employee);
          setDialogOpen(true);
        }}
        onDelete={(employee) => {
          setSelectedEmployee(employee);
          setDeleteRelated(false);
          setDeleteOpen(true);
        }}
      />

      <EmployeeDialog
        open={dialogOpen}
        employee={selectedEmployee}
        isSaving={isSaving}
        onClose={() => {
          if (isSaving) {
            return;
          }
          setDialogOpen(false);
          setSelectedEmployee(null);
        }}
        onSave={handleSave}
      />

      <DeleteEmployeeDialog
        open={deleteOpen}
        isDeleting={isDeleting}
        employee={selectedEmployee}
        deleteRelated={deleteRelated}
        onToggleDeleteRelated={setDeleteRelated}
        onClose={() => {
          if (isDeleting) {
            return;
          }
          closeDeleteDialog();
        }}
        onConfirm={handleDelete}
      />

      <Dialog
        open={bulkDeleteOpen}
        onClose={() => {
          if (isBulkDeleting) {
            return;
          }
          setBulkDeleteOpen(false);
        }}
        disableEscapeKeyDown={isBulkDeleting}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Selected Employees</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all selected employees?
            <br />
            <br />
            Selected: <strong>{selectedEmployeeIds.length}</strong>
            <br />
            This will remove related records for each selected employee.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setBulkDeleteOpen(false)}
            disabled={isBulkDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            disabled={isBulkDeleting}
            startIcon={
              isBulkDeleting
                ? <CircularProgress size={16} color="inherit" />
                : <DeleteSweepIcon />
            }
          >
            {isBulkDeleting ? "Deleting..." : "Delete All"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default Employees;
